import shaka from "shaka-player";

export type ShakaPlaybackCallbacks = {
  onBuffering?: (buffering: boolean) => void;
  onError?: (error: unknown) => void;
  onReady?: () => void;
  onVariantChanged?: (height: number, bandwidth: number) => void;
};

export type ShakaPlaybackController = {
  destroy: () => Promise<void>;
};

let polyfillsInstalled = false;

export async function attachShakaPlayer(
  video: HTMLVideoElement,
  source: string,
  callbacks: ShakaPlaybackCallbacks = {},
): Promise<ShakaPlaybackController | null> {
  if (!polyfillsInstalled) {
    shaka.polyfill.installAll();
    polyfillsInstalled = true;
  }
  if (!shaka.Player.isBrowserSupported()) return null;

  const player = new shaka.Player(video);
  player.configure({
    abr: {
      enabled: true,
      defaultBandwidthEstimate: 12_000_000,
      switchInterval: 4,
    },
    streaming: {
      bufferingGoal: 24,
      rebufferingGoal: 2,
      bufferBehind: 12,
      retryParameters: {
        maxAttempts: 4,
        baseDelay: 500,
        backoffFactor: 2,
        fuzzFactor: 0.25,
        timeout: 30_000,
        stallTimeout: 5_000,
        connectionTimeout: 10_000,
      },
    },
    restrictions: {
      maxHeight: Infinity,
      maxWidth: Infinity,
      maxBandwidth: Infinity,
    },
    preferredVideoCodecs: ["av01", "hvc1", "hev1", "avc1"],
  });

  player.addEventListener("buffering", (event: Event) => {
    const buffering = Boolean(
      (event as Event & { buffering?: boolean }).buffering,
    );
    callbacks.onBuffering?.(buffering);
  });
  player.addEventListener("error", (event: Event) => {
    callbacks.onError?.((event as Event & { detail?: unknown }).detail || event);
  });
  player.addEventListener("variantchanged", () => {
    const active = player.getVariantTracks().find((track) => track.active);
    callbacks.onVariantChanged?.(
      Number(active?.height || 0),
      Number(active?.bandwidth || 0),
    );
  });

  try {
    await player.load(source);
    callbacks.onReady?.();
    return { destroy: () => player.destroy() };
  } catch (error) {
    await player.destroy().catch(() => {});
    throw error;
  }
}
